import React, { useCallback, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Link,
  Typography,
} from '@mui/material'
import {
  AccordionComponent,
  AccordionSectionProps,
  ButtonV2,
  SidePanelInfo,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './CreateStackHelpFaqs.styles'
import {
  FaqQuestion,
  HelpDrawerFaqItems,
} from '../../AddEditProductFormComponents/FlyoutProductFormHelpFaqs/ProductCreationFaqUtil'

const CLOUDFORMATION_SECTION_KEY = 'cloudformation'
const TERRAFORM_SECTION_KEY = 'terraform'

const CREATE_STACK_CLOUDFORMATION_FAQ_SECTION =
  'createStack.cloudformationSetup'
const CREATE_STACK_TERRAFORM_FAQ_SECTION = 'createStack.terraformSetup'

const cloudformationFaqItems: HelpDrawerFaqItems[] = [
  {
    question: (
      <FaqQuestion
        section={CREATE_STACK_CLOUDFORMATION_FAQ_SECTION}
        question="1. What option should I choose for “Have you already set up Data Feeds in your own S3 bucket?"
      />
    ),
    answer: (
      <div>
        Proceed with “No” if you have not set up data feeds in your own S3
        bucket. In this case, you will need to run the CloudFormation Template
        (CFT) to complete the setup.
        <br />
        Select “Yes” if you have already configured data feeds in your own S3
        bucket (including S3 Bucket ARN and KMS Key ARN, SNS topic ARN).
        <br />
        We recommend choosing “No” if you are unsure or setting this up for the
        first time.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={CREATE_STACK_CLOUDFORMATION_FAQ_SECTION}
        question="2. What are the permissions that Labra receives when I run the cloud formation stack?"
      />
    ),
    answer: (
      <div>
        Labra requires basic permission to help you manage your cloud
        marketplace operations. To learn more about the permission.{' '}
        <Link
          href="https://helpcenter.labra.io/hc/en-us/articles/42055500734995-Marketplace-cloud-formation-stack-explanation"
          target="_blank"
          rel="noopener noreferrer"
        >
          Check this article
        </Link>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={CREATE_STACK_CLOUDFORMATION_FAQ_SECTION}
        question="3. How can I get this information?"
      />
    ),
    answer: (
      <div>
        <b> If ‘No’ selected: </b>
        <div>Please follow the steps mentioned below:</div>
        <div>
          Step 1 - Log in to the AWS Console account that you will use to create
          the IAM role (this must be a Marketplace account).
        </div>
        <div>
          Step 2 - Click on ‘Launch Cloudformation Stack’, once inside
          Cloudformation&apos;s &quot;Create Stack&quot; page. Values of Stack
          name(unique value), External ID(do not update this value) and Partner
          Org name (unique value) are already filled.
        </div>
        <div>
          Step 3 - Acknowledge that AWS CloudFormation might create IAM
          resources with custom names and then click on ‘Create Stack’ CTA.
        </div>
        <div>
          Step 4 - Go to the Outputs tab once the creation status is Completed,
          copy the values of corresponding keys and enter them in the correct
          fields.
        </div>
        <div>
          Step 5 - Verify that the connection is established successfully.
        </div>
        <div>
          Step 6 - Customer data storage feeds will be automatically filled on
          AMMP after all field values are filled in Labra.
        </div>
        <b> If ‘Yes’ selected: </b>
        <div>Please follow the steps mentioned below:</div>
        <div>
          Step 1 - Log in to the AWS Console account that you will use to create
          the IAM role (this must be a Marketplace account).
          <br />
          If “Yes” is selected, you must run two CloudFormation Templates
          (CFTs): one for the Platform Role ARN and one for Data Feeds.
        </div>
        <b>Platform role ARN</b>
        <div>
          Step 2 - Click on ‘Launch Cloudformation Stack’, and once inside
          Cloudformation&apos;s &quot;Create Stack&quot; page. Values of Stack
          name(unique value), External ID(do not update this value) and Partner
          Org name (unique value) are already filled.
        </div>
        <div>
          Step 3 - Acknowledge that AWS CloudFormation might create IAM
          resources with custom names and then click on ‘Create Stack’ CTA.
        </div>
        <div>
          Step 4 - Once stack created successfully,go to the Outputs tab and
          once the creation status is Completed, copy the values of
          corresponding Platform role and enter them in the correct fields in
          Labra platform.
        </div>
        <div>
          Step 5 - Verify that the connection is established successfully.
        </div>
        <b>Data Feeds</b>
        <div>
          Step 6 - Go to the given link, copy each field value under “Setup
          customer data storage,” and paste the values into the corresponding
          fields in Labra platform.
        </div>
        <div>
          Step 7 - If the SNS Topic ARN is not added in Customer Data Storage in
          AWS, note that this field is mandatory in Labra. If it is not
          available in the AWS Console, follow these steps: Go to CloudFormation
          in the AWS Console. Select the relevant stack. Navigate to the Outputs
          tab. Copy the SNSTopicARN value. Paste this value into the Customer
          Data Storage section in the AWS Console and in Labra.
        </div>
        <div>
          Step 8 - Click on ‘Launch Cloudformation Stack’. Values are
          pre-filled, Click on ‘Create stack’
        </div>
        <div>
          Step 9 - Once stack created successfully, Go to Outputs, you can find
          those values added in Outputs.
        </div>
        <div>
          Step 10 - Verify that the connection is established successfully
        </div>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={CREATE_STACK_CLOUDFORMATION_FAQ_SECTION}
        question="4. What are data feeds (S3 bucket ARN, KMS key ARN, SNS topic ARN) ?"
      />
    ),
    answer: (
      <div>
        Data feeds are used by AWS to report AWS Marketplace data regarding
        contracts, revenue and disbursements.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={CREATE_STACK_CLOUDFORMATION_FAQ_SECTION}
        question="5. How is data feeds configured and how does Labra use it?"
      />
    ),
    answer: (
      <div>
        Data feeds are configured by creating an S3 bucket, KMS key and an SNS
        topic. S3 bucket is used to store data that AWS sends. KMS key is used
        to allow encryption. SNS topic is used to notify that new data is added
        in the bucket.
        <br />
        <br />
        Labra subscribes to the SNS topic and reads data from the S3 bucket.
        This data is then presented to you in the dashboard and revenue section.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={CREATE_STACK_CLOUDFORMATION_FAQ_SECTION}
        question="6. How often is data updated in the S3 bucket?"
      />
    ),
    answer: <div>Data gets updated every 24 hours.</div>,
  },
]

const terraformFaqItems: HelpDrawerFaqItems[] = [
  {
    question: (
      <FaqQuestion
        section={CREATE_STACK_TERRAFORM_FAQ_SECTION}
        question="1. When should I use Terraform?"
      />
    ),
    answer: (
      <div>
        Use Terraform if your organization manages AWS infrastructure using
        Terraform. If you are unsure which option to choose, we recommend using
        CloudFormation.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={CREATE_STACK_TERRAFORM_FAQ_SECTION}
        question="2. What information do I need to provide in Labra?"
      />
    ),
    answer: (
      <div>
        After running the Terraform template successfully, provide the following
        values in Labra:
        <ul>
          <li>Platform Role ARN</li>
          <li>S3 Bucket ARN</li>
          <li>KMS Key ARN</li>
          <li>SNS Topic ARN</li>
        </ul>
        These values are generated as Terraform outputs.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={CREATE_STACK_TERRAFORM_FAQ_SECTION}
        question="3. What happens after I complete the Terraform setup?"
      />
    ),
    answer: (
      <div>
        Once all required values are entered. Click on ‘Validate connection’
        CTA. Once connection is validated successfully. You will be able to
        create and manage listings via Labra and track invoices and
        disbursements.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={CREATE_STACK_TERRAFORM_FAQ_SECTION}
        question='4. Can I use Terraform if I select "Yes"?'
      />
    ),
    answer: (
      <div>
        If you have your own S3 bucket and would like to onboard Labra using
        Terraform, please contact the Customer Success team for assistance.
      </div>
    ),
  },
]

const sectionAccordionGroups: {
  key: string
  sectionAccordionTitle: string
  faqItems: HelpDrawerFaqItems[]
}[] = [
  {
    key: CLOUDFORMATION_SECTION_KEY,
    sectionAccordionTitle: 'Cloudformation setup',
    faqItems: cloudformationFaqItems,
  },
  {
    key: TERRAFORM_SECTION_KEY,
    sectionAccordionTitle: 'Terraform setup',
    faqItems: terraformFaqItems,
  },
]

const mapFaqItemsToSections = (
  items: HelpDrawerFaqItems[]
): AccordionSectionProps[] =>
  items.map((item, idx) => ({
    key: `section-${idx}`,
    heading: item.question,
    description: item.answer,
    enableListStyle: true,
  }))

const buildFaqAccordion = (
  items: HelpDrawerFaqItems[],
  nestedFaqAccordionClass: string
) => (
  <div className={nestedFaqAccordionClass}>
    <AccordionComponent
      minWidth="100%"
      minHeight="auto"
      sections={mapFaqItemsToSections(items)}
      disableBorder
      aligned
    />
  </div>
)

export const CreateStackHelpFaqs: React.FC = () => {
  const { classes } = useStyles()
  const [isOpen, setIsOpen] = useState(false)
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false)
  const [expandedSection, setExpandedSection] = useState<string | false>(false)
  const isMounted = isOpen || hasOpenedOnce

  const handleOpen = useCallback(() => {
    setHasOpenedOnce(true)
    setIsOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setExpandedSection(false)
  }, [])

  const handleSectionChange =
    (sectionKey: string) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedSection(isExpanded ? sectionKey : false)
    }

  return (
    <>
      <div>
        <ButtonV2
          styleType="link"
          data-testid="help-button-drawer"
          icon={
            <FontAwesomeIcon
              icon={faCircleInfo}
              style={{ height: '16px', width: '16px', fontSize: '16px' }}
            />
          }
          onClick={handleOpen}
        >
          Help & FAQs
        </ButtonV2>
      </div>
      {isMounted && (
        <SidePanelInfo
          icon={
            <FontAwesomeIcon
              icon={faCircleInfo}
              style={{ width: '20px', height: '20px', fontSize: '20px' }}
            />
          }
          headerTitle="Help"
          title="Create stack"
          open={isOpen}
          onClose={handleClose}
          content={
            <>
              <div className={classes.divider} />
              <div
                className={classes.sectionsWrapper}
                data-testid="create-stack-help-sections"
              >
                {sectionAccordionGroups.map(group => (
                  <Accordion
                    key={group.key}
                    data-testid={`create-stack-help-section-${group.key}`}
                    expanded={expandedSection === group.key}
                    onChange={handleSectionChange(group.key)}
                    disableGutters
                    elevation={0}
                    square
                    className={classes.sectionAccordion}
                    slotProps={{ transition: { unmountOnExit: true } }}
                  >
                    <AccordionSummary
                      className={classes.sectionSummary}
                      expandIcon={
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={classes.chevron}
                          aria-hidden
                        />
                      }
                      aria-controls={`${group.key}-content`}
                      id={`${group.key}-header`}
                    >
                      <Typography className={classes.sectionHeading}>
                        {group.sectionAccordionTitle}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.sectionDetails}>
                      {buildFaqAccordion(
                        group.faqItems,
                        classes.nestedFaqAccordion
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>
            </>
          }
        />
      )}
    </>
  )
}
